// Copyright (c) 2019 embyt GmbH. All rights reserved.
// Author: Roman Morawek
import * as React from "react";
import { connect } from "react-redux";
import { RoutedProps, withRouteRouter } from "view/reactrouterinterceptor";

import { DataState, ForegroundPanel, MainPanel } from "types";
import { isLocalhost } from "guiutils";
import { isSet } from "shared/utils/utils";
import { IState } from "states/states";
import { IAction, raiseAction, ActionType } from "actions";
import { AppState } from "states/schedulerstates";

import ConfirmModal from "view/comp/ConfirmModal";
import Navigation from "./Navigation";
import Login from "view/page/Login";
import SignUp from "view/page/SignUp";
import UserPwdForgot from "view/comp/UserPwdForgot";
import UserPwdChange from "view/comp/UserPwdChange";
import UserMfaCode from "view/comp/UserMfaCode";
import SiteSelection from "view/page/SiteSelection";
import ContentPanel from "./ContentPanel";

import SiteAnimation from "view/page/SiteAnimation";

// apply style definitions
import "content.scss";

// uses webpack definitions
declare const IS_CLOUD: boolean;

// pages with small tables get a limited width, so labels and values stay visually connected
const NARROW_PANELS = [
  MainPanel.IOS,
  MainPanel.ALARMS,
  MainPanel.MODULES,
  MainPanel.ENERGYMETERS,
  MainPanel.USER_SETTINGS,
  MainPanel.SITE_SETTINGS,
];

// the touch keyboard only renders on non-cloud localhost builds, so keep it and its
// jQuery dependency out of the main bundle and load it on demand instead
const Keyboard = React.lazy(() => import("view/comp/Keyboard"));

interface IRootProps {
  hasTouchKeyboard: boolean;
  isInitDone: boolean;
  siteTitle: string | undefined;
  panel: MainPanel | undefined;
  appState: AppState;
  foregroundPanel?: ForegroundPanel;
  loginDataState: DataState;
  willAutoSelect: boolean;
}

const mapStateToProps = (state: IState) => {
  const site = state.site.siteList?.find((curItem) => curItem.id === state.cached.selectedSite);
  const siteTitle =
    IS_CLOUD ?
      isSet(site) ? `enline cloud - ${site.name}`
      : "enline cloud"
    : undefined;

  const props: IRootProps = {
    hasTouchKeyboard: !IS_CLOUD && isLocalhost(),
    isInitDone: state.scheduler.appState >= AppState.READY,
    siteTitle,
    panel: state.router.panel,
    appState: state.scheduler.appState,
    foregroundPanel: state.gui.foregroundPanel,
    loginDataState: state.scheduler.loginDataState,
    willAutoSelect:
      state.site.siteList?.length === 1 ||
      state.site.siteList?.some((s) => s.id === state.cached.selectedSite) === true,
  };
  return props;
};

interface IRootDispatch {
  onVisibilityChange: () => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IRootDispatch = {
    onVisibilityChange: () => {
      const isVisible = document.visibilityState === "visible";
      dispatch(raiseAction(ActionType.ACT_VISIBILITY_CHANGE, isVisible));
    },
  };
  return dispatchProps;
};

class RootComponent extends React.Component<IRootProps & RoutedProps & IRootDispatch> {
  public componentDidMount() {
    document.addEventListener("visibilitychange", this.props.onVisibilityChange);
  }
  public componentWillUnmount() {
    document.removeEventListener("visibilitychange", this.props.onVisibilityChange);
  }

  public componentDidUpdate() {
    if (this.props.siteTitle) {
      document.title = this.props.siteTitle;
    }
  }

  private renderPanel(): React.ReactNode {
    switch (this.props.foregroundPanel) {
      case ForegroundPanel.SIGNUP:
      case ForegroundPanel.SIGNUP_CONFIRM:
        return <SignUp />;
      case ForegroundPanel.FORGOT_PASSWORD: // intentional fall-through
      case ForegroundPanel.PASSWORD_CONFIRM:
        return <UserPwdForgot />;
      case ForegroundPanel.PASSWORD_CHANGE:
        return <UserPwdChange />;
      case ForegroundPanel.MFA_CODE:
        return <UserMfaCode />;
    }
    // suppress login page during the initial auto-login check (loginDataState = EMPTY)
    if (
      this.props.appState === AppState.LOGGED_OUT &&
      (!IS_CLOUD || this.props.loginDataState !== DataState.EMPTY)
    ) {
      return <Login />;
    }
    // suppress site selection if the saga will auto-select
    if (this.props.appState === AppState.SITE_LIST_LOADED && !this.props.willAutoSelect) {
      // the user pages are the only ones working without a selected site, keep them routed
      const isUserPage = [MainPanel.USER, MainPanel.USER_SETTINGS].includes(this.props.panel!);
      return isUserPage ? null : <SiteSelection />;
    }
    if (this.props.appState < AppState.READY) {
      return <SiteAnimation />;
    }
    return null;
  }

  public render() {
    const panel = this.renderPanel();
    // the loading animation (e.g. during the auto-login check on reload) keeps the regular
    // layout, so it stays at the same position as the loading screen from index.html
    const isAuthPage =
      this.props.appState < AppState.LOGGED_IN &&
      !(React.isValidElement(panel) && panel.type === SiteAnimation);

    // special handling for custom dashboard page, as this shall be shown fullscreen
    const isFullscreen = this.props.panel === MainPanel.CUSTOM_DASHBOARD || isAuthPage;
    const hasContainer =
      !isAuthPage &&
      (panel !== null ||
        ![MainPanel.PUBLIC_DISPLAY, MainPanel.CUSTOM_DASHBOARD].includes(this.props.panel!));
    const isNarrow = panel === null && NARROW_PANELS.includes(this.props.panel!);

    return (
      <>
        <header className={isFullscreen ? "noHeader" : undefined}>
          {!isFullscreen && <Navigation showNavigation={this.props.isInitDone} />}
        </header>
        <main className={hasContainer ? `container${isNarrow ? " narrow-page" : ""}` : undefined}>
          {panel ?? <ContentPanel />}
        </main>
        <ConfirmModal />
        {this.props.hasTouchKeyboard && (
          <React.Suspense fallback={null}>
            <Keyboard />
          </React.Suspense>
        )}
      </>
    );
  }
}

const Root = connect(mapStateToProps, mapDispatchToProps)(withRouteRouter(RootComponent));

export default Root;
